/**
 * Parser del MRZ TD1 (ICAO 9303 parte 5) del dorso del DNI argentino tarjeta:
 * 3 líneas × 30 caracteres, charset A-Z 0-9 '<', check digits con pesos 7-3-1
 *
 * Qué trae y qué NO trae, que importa para saber de dónde puede salir cada dato:
 *
 *   línea 1: tipo(2) país(3) documento(9) verif(1) opcional(15)
 *   línea 2: nacimiento(6) verif(1) sexo(1) vencimiento(6) verif(1) país(3)
 *            opcional(11) compuesto(1)
 *   línea 3: apellido<<nombres, 30 caracteres para los dos
 *
 * NO hay fecha de emisión. Esa solo puede venir del texto impreso o del código
 * de barras. Y el verificador compuesto cubre la línea 1 y los tramos 1-7, 9-15
 * y 19-29 de la línea 2: deja AFUERA el sexo y la nacionalidad.
 * (A=10..Z=35, '<'=0). Todos los modelos vigentes (tarjeta 2012+, nuevo 2026)
 * lo traen; las libretas no son válidas desde 04/2017.
 *
 * Los check digits son la prueba de LEGIBILIDAD de la foto: si doc, nacimiento
 * y compuesto verifican, la lectura es confiable y los campos se pueden
 * comparar contra los datos cargados del autor.
 *
 * ESPEJO byte-idéntico en DOS repos (cambios en uno = cambios en el otro, con
 * los mismos vectores de test en ambas suites):
 *   jupiter-server/src/common/dniUpload/mrzTd1.ts               (gate server)
 *   jupiter-client/src/common/pages/dniUpload/mrzTd1.ts        (browser)
 */

export interface MrzTd1Data {
  docType: string;
  issuingCountry: string;
  /** Número de documento sin fillers '<' (el DNI: 7 u 8 dígitos). */
  documentNumber: string;
  /** ISO "YYYY-MM-DD" (pivote de siglo: YY > año actual → 19YY). */
  birthDate: string;
  expiryDate: string | null;
  sex: "M" | "F" | "X" | null;
  /**
   * Nacionalidad (línea 2, posiciones 16-18). Se lee pero NO se verifica: el
   * dígito compuesto del TD1 cubre la línea 1 y los tramos 1-7, 9-15 y 19-29
   * de la línea 2, así que ésta queda afuera igual que el sexo.
   */
  nationality: string | null;
  /** ASCII upper del MRZ, '<' → espacio ("PEREZ", "NANDU GOMEZ"). */
  surname: string;
  givenNames: string;
  /**
   * La línea de nombres reducida a letras, como la leyó el OCR. El '<' es el
   * separador del MRZ y es justo lo que el OCR confunde con L/K, así que los
   * nombres no se pueden partir de forma confiable: el matching compara contra
   * esto, donde las letras de los nombres SÍ están.
   */
  nameLineRaw: string;
  /** `null` = no se pudo leer ese dígito (no es lo mismo que leerlo mal). */
  checks: { doc: boolean; birth: boolean; expiry: boolean; composite: boolean | null };
  /**
   * Las tres líneas CANÓNICAS (30 caracteres, charset del MRZ). Cuando la
   * lectura vino ruidosa son la reconstrucción, no el texto crudo del OCR: es
   * lo que el browser le manda al server para que las vuelva a verificar
   * contra la base sin tener que re-interpretar ruido.
   */
  raw1: string;
  raw2: string;
  raw3: string;
}

const CONFUSIONES: Record<string, string> = {
  B: "8", O: "0", I: "1", L: "1", S: "5", Z: "2", G: "6", Q: "0",
};

/**
 * Repara letras que el OCR puso donde iba un dígito, SOLO en la zona numérica.
 *
 * El mapeo no puede aplicarse a la línea entera: "G" va a "6" y eso convierte el
 * "ARG" del país en "AR6", con lo cual el patrón deja de reconocer la línea.
 * Esa fue exactamente la forma de romperlo la primera vez.
 */
function digitosDeZonaNumerica(zona: string): string {
  return zona.replace(/[BOILSZGQ]/g, (c) => CONFUSIONES[c] ?? c);
}

const WEIGHTS = [7, 3, 1];

function charValue(c: string): number {
  if (c === "<") return 0;
  if (c >= "0" && c <= "9") return c.charCodeAt(0) - 48;
  return c.charCodeAt(0) - 55; // A=10 .. Z=35
}

/** Check digit ICAO: suma de valor×peso(7,3,1 cíclico) módulo 10. */
export function mrzCheckDigit(field: string): number {
  let sum = 0;
  for (let i = 0; i < field.length; i++) {
    sum += charValue(field[i]) * WEIGHTS[i % 3];
  }
  return sum % 10;
}

function checkEquals(field: string, digit: string): boolean {
  if (digit < "0" || digit > "9") return false;
  return mrzCheckDigit(field) === digit.charCodeAt(0) - 48;
}

/**
 * Zonas numéricas de cada línea (para reparar confusiones típicas del OCR
 * O→0 / I→1 sin tocar los nombres): línea 1 = nro de documento + su check
 * (posiciones 5..14, el DNI es numérico); línea 2 = nacimiento+check (0..6),
 * vencimiento+check (8..14) y check compuesto (29).
 */
const NUMERIC_ZONES: Array<Array<[number, number]>> = [
  [[5, 15]],
  [[0, 7], [8, 15], [29, 30]],
  [],
];

function repairNumericZones(line: string, lineIndex: number): string {
  const zones = NUMERIC_ZONES[lineIndex] ?? [];
  const chars = line.split("");
  for (const [from, to] of zones) {
    for (let i = from; i < to && i < chars.length; i++) {
      if (chars[i] === "O") chars[i] = "0";
      else if (chars[i] === "I") chars[i] = "1";
    }
  }
  return chars.join("");
}

/**
 * Extrae las 3 líneas del MRZ de un texto OCR con ruido: limpia espacios
 * internos (el OCR los inyecta), exige charset [A-Z0-9<] y largo 30, y toma
 * las primeras 3 candidatas consecutivas. Repara O→0/I→1 en zonas numéricas.
 */
export function findMrzLines(rawOcrText: string): [string, string, string] | null {
  const candidates: string[] = [];
  for (const rawLine of rawOcrText.split(/\r?\n/)) {
    const cleaned = rawLine.replace(/\s+/g, "").toUpperCase();
    if (cleaned.length === 30 && /^[A-Z0-9<]+$/.test(cleaned)) {
      candidates.push(cleaned);
    } else if (candidates.length > 0 && candidates.length < 3) {
      // las 3 líneas del MRZ son consecutivas; un corte en el medio reinicia
      if (cleaned.length > 0) candidates.length = 0;
    }
    if (candidates.length === 3) break;
  }
  if (candidates.length !== 3) return null;
  return [
    repairNumericZones(candidates[0], 0),
    repairNumericZones(candidates[1], 1),
    repairNumericZones(candidates[2], 2),
  ];
}

function toIsoDate(yymmdd: string, kind: "birth" | "expiry"): string | null {
  if (!/^\d{6}$/.test(yymmdd)) return null;
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const currentYY = new Date().getFullYear() % 100;
  // Nacimientos: nadie nace en el futuro → YY mayor al año actual es 19YY.
  // Vencimientos: siempre futuros cercanos → 20YY.
  const century = kind === "birth" ? (yy > currentYY ? 1900 : 2000) : 2000;
  const anio = century + yy;
  const mes = parseInt(yymmdd.slice(2, 4), 10);
  const dia = parseInt(yymmdd.slice(4, 6), 10);
  // Seis dígitos no son una fecha. Sin este control salían cosas como
  // 2020-11-33 y 2001-07-99, que es lo que trae el MRZ inventado de los
  // especímenes del RENAPER: se mostraban como si fueran fechas de verdad.
  if (mes < 1 || mes > 12 || dia < 1) return null;
  const ultimoDia = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
  if (dia > ultimoDia) return null;
  return `${anio}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
}

/**
 * Parte la línea 3 en apellido y nombres.
 *
 * El separador es DOBLE: "<<" entre apellido y nombres, "<" simple entre las
 * palabras de cada uno. "GONZALEZ<RODRIGUEZ<<MARIANA<FL" es apellido
 * "GONZALEZ RODRIGUEZ" y nombres "MARIANA FL".
 *
 * CUANDO LA LÍNEA TIENE SU "<<", LAS PALABRAS SON PALABRAS y no se toca nada.
 * Aplicarle las heurísticas de relleno a una línea sana hacía daño real:
 * "DE<LA<TORRE" perdía las partículas y quedaba en "TORRE", y "MANUEL" perdía
 * su última letra porque termina en L, que es una de las que el OCR pone donde
 * va un "<". Esas heurísticas existen para líneas ROTAS y solo ahí se aplican.
 */
function namesFrom(line3: string): { surname: string; givenNames: string } {
  const palabras = (trozo: string) =>
    trozo
      .split("<")
      .map((p) => sacarRelleno(p.replace(/[^A-Z]/g, "")))
      .filter(Boolean)
      .join(" ");

  // Que haya un "<<" no prueba que sea EL separador: en una línea rota el OCR
  // se come los separadores reales y el primer "<<" que aparece es el comienzo
  // del relleno del final. Medido sobre un DNI de 2010:
  // "OVIRA<K<RODOLFOSMANUEL<<<LLLLL" daba apellido "OVIRA K RODOLFOSMANUEL" y
  // nombres "LLLLL".
  //
  // Se comprueba: si lo que queda de un lado no parece un nombre —sin una sola
  // vocal— ese "<<" era relleno y hay que ir por el camino tolerante.
  const pareceNombre = (t: string) => t.length >= 2 && /[AEIOU]/.test(t);
  const corteDoble = line3.indexOf("<<");
  if (corteDoble > 0) {
    const apellido = palabras(line3.slice(0, corteDoble));
    const nombres = palabras(line3.slice(corteDoble + 2));
    if (pareceNombre(apellido) && pareceNombre(nombres)) {
      return { surname: apellido, givenNames: nombres };
    }
  }

  // Sin separador doble la línea está corrompida: el OCR leyó los '<' como
  // letras. Ahí sí hay que adivinar, y se corta en el primer separador que haya.
  const clean = (t: string) => t.replace(/</g, " ").replace(/\s+/g, " ").trim();
  const terminaEnLetra = /[A-Z]$/.test(line3.trim());
  const corte = line3.search(/[^A-Z]/);
  return {
    surname: limpiarRelleno(clean(corte > 0 ? line3.slice(0, corte) : line3)),
    givenNames: limpiarRelleno(clean(corte > 0 ? line3.slice(corte) : ""), !terminaEnLetra),
  };
}

export function parseMrzTd1(lines: [string, string, string]): MrzTd1Data | null {
  const [l1, l2, l3] = lines;
  if (l1?.length !== 30 || l2?.length !== 30 || l3?.length !== 30) return null;

  const docNumberField = l1.slice(5, 14);
  const birthField = l2.slice(0, 6);
  const expiryField = l2.slice(8, 14);
  const sexChar = l2[7];
  const nationalityField = l2.slice(15, 18).replace(/</g, "").trim();

  // Compuesto ICAO TD1: línea1[5..30) + línea2[0..7) + línea2[8..15) + línea2[18..29)
  const compositeInput = l1.slice(5, 30) + l2.slice(0, 7) + l2.slice(8, 15) + l2.slice(18, 29);

  const checks = {
    doc: checkEquals(docNumberField, l1[14]),
    birth: checkEquals(birthField, l2[6]),
    expiry: checkEquals(expiryField, l2[14]),
    composite: checkEquals(compositeInput, l2[29]),
  };

  const { surname, givenNames } = namesFrom(l3);

  return {
    docType: l1.slice(0, 2).replace(/</g, ""),
    issuingCountry: l1.slice(2, 5).replace(/</g, ""),
    documentNumber: docNumberField.replace(/</g, ""),
    birthDate: toIsoDate(birthField, "birth") ?? "",
    expiryDate: toIsoDate(expiryField, "expiry"),
    sex: sexChar === "M" || sexChar === "F" ? sexChar : sexChar === "X" ? "X" : null,
    nationality: nationalityField.length > 0 ? nationalityField : null,
    surname,
    givenNames,
    nameLineRaw: l3.replace(/[^A-Z]/g, ""),
    checks,
    raw1: l1,
    raw2: l2,
    raw3: l3,
  };
}

/**
 * Legible = el número y la fecha de nacimiento verifican su propio dígito.
 *
 * El compuesto ya no es obligatorio, y la razón es empírica: es el ÚLTIMO
 * carácter de la línea 2, el que el OCR pierde con más frecuencia (lo devuelve
 * como letra). Exigirlo rechazaba fotos nítidas de documentos legítimos. Lo que
 * se compara contra la base —número y fecha— tiene cada uno su verificador, y
 * además tiene que COINCIDIR con el autor: un OCR corrupto no produce por
 * casualidad el número exacto de esa persona. Si el compuesto SÍ se leyó y no
 * verifica, eso sí es señal de lectura corrupta y se rechaza.
 */
export function isMrzLegible(d: MrzTd1Data): boolean {
  if (!d.checks.doc || !d.checks.birth) return false;
  return d.checks.composite !== false;
}

/**
 * Lee el MRZ de un texto de OCR REAL, que es otra cosa que un MRZ.
 *
 * Medido con `scripts/test/ocr-dni.js` sobre fotos de DNI nítidas: tesseract
 * acierta los datos —número, fechas, nombres— y falla sistemáticamente en los
 * '<' de relleno, que devuelve como L, K, Y o S, y agrega caracteres de más al
 * final de cada línea. Pedir 30 caracteres exactos del charset del MRZ, como
 * hace `findMrzLines`, descartaba por eso lecturas enteramente correctas.
 *
 * Acá el ancla es la ESTRUCTURA del TD1, que el ruido no toca: la línea 2 tiene
 * una forma inconfundible (seis dígitos, verificador, sexo, seis dígitos,
 * verificador, país) y la 1 empieza con ID + país + número. Se reconstruyen las
 * líneas canónicas a partir de los campos encontrados y se deja que los dígitos
 * verificadores digan si la lectura sirve.
 */
/**
 * Corta los nombres donde empieza el relleno.
 *
 * El OCR devuelve los '<' de relleno como L, K, S o Y, así que de
 * "ROVIRA<<RODOLFO<MANUEL<<<<<<<" salía "RODOLFO MANUEL L LKFG". Una palabra
 * de una sola letra en la línea de nombres del MRZ es relleno mal leído: los
 * nombres van escritos completos. Desde ahí, todo lo que sigue se descarta.
 */
/**
 * Saca el relleno del MRZ que el OCR leyó como letras.
 *
 * Los '<' salen como K, L o Y, y quedan pegados a los nombres: medido sobre un
 * dorso real, "WOLFRAM<<ELENA<<<<" salió "WOLFRAMK KELENA LLLLLLLKKLK".
 *
 * Dos limpiezas, las dos conservadoras:
 *   - se tiran los pedazos de tres o más letras que son SOLO relleno (sin
 *     vocales): "LLLLLLLKKLK" no es un nombre;
 *   - se recorta una K, L o Y suelta al principio o al final de una palabra de
 *     cinco letras o más.
 *
 * La S queda afuera del recorte a propósito, porque muchos apellidos terminan
 * en S. El riesgo que queda es un nombre que empiece o termine en K, L o Y: es
 * poco frecuente, y de todos modos el código de barras y el frente dan el
 * nombre completo cuando están.
 */
function sacarRelleno(palabra: string): string {
  // SOLO se descarta lo que es todo relleno. Recortar además una K, L o Y del
  // borde de las palabras largas dañaba lecturas correctas: "MANUEL" quedaba en
  // "MANUE". Que un token sea íntegramente relleno es una señal fuerte; que una
  // palabra termine en L no lo es, y no hay forma de distinguirla de un nombre.
  //
  // Lo que quede de más se ve, y si otra fuente da el nombre completo el
  // consolidador prefiere el más largo.
  if (/^[KLSYX<]{3,}$/.test(palabra)) return "";
  return sacarRellenoPegado(palabra);
}

/**
 * Corta el relleno que quedó PEGADO al nombre, sin separador que lo delate.
 *
 * Cuando el OCR lee TODOS los '<' del final como letras, el relleno y el nombre
 * son un solo token y no hay espacio donde cortar. Medido sobre un dorso real:
 * el nombre "SOFIA" salió "SOFIAKLLLLLLLLLLLLRKLS".
 *
 * La señal es una LETRA REPETIDA TRES VECES. No existe en un nombre —ni en
 * ningún idioma con alfabeto latino— y sí en el relleno, que son doce '<'
 * seguidos leídos como la misma letra. Ahí se corta, y desde el corte se camina
 * hacia atrás mientras las letras sean de las que el OCR pone en lugar del '<':
 * lo que está pegado a un relleno confirmado es relleno también.
 *
 * Por qué NO se recorta una K o una L final a secas: se probó y rompía lecturas
 * correctas —"MANUEL" quedaba en "MANUE"—. Sin la letra repetida no hay señal,
 * y sin señal no se toca.
 */
function sacarRellenoPegado(palabra: string): string {
  const repetida = palabra.match(/([A-Z])\1{2,}/);
  if (!repetida || repetida.index === undefined) return palabra;
  let corte = repetida.index;
  while (corte > 0 && "KLSYX".includes(palabra[corte - 1])) corte--;
  return corte >= 2 ? palabra.slice(0, corte) : palabra;
}

/**
 * @param hayRellenoAlFinal si después del último nombre viene relleno.
 *
 * Es lo que distingue un fragmento TRUNCADO de basura del OCR, y la distinción
 * sale del formato y no de un caso: la línea 3 tiene exactamente 30 caracteres.
 * Si el nombre llega hasta el final, lo último está cortado por el largo —de
 * "FLORENCIA" queda "FL"— y es un dato, no ruido. Si lo sigue relleno, el
 * pedacito corto es un '<' mal leído.
 *
 * Descartar por largo a secas —que era lo que hacía— tiraba el fragmento
 * truncado y escondía que el nombre estaba incompleto.
 */
function limpiarRelleno(nombres: string, hayRellenoAlFinal = true): string {
  const partes = nombres.split(/\s+/).map(sacarRelleno).filter(Boolean);
  if (!hayRellenoAlFinal) {
    // Sin relleno detrás, nada de lo que hay es relleno: solo se saltea lo que
    // el OCR pudo dejar ANTES del primer nombre.
    let desde = 0;
    while (desde < partes.length - 1 && partes[desde].length <= 2) desde++;
    return partes.slice(desde).join(" ").trim();
  }
  // El relleno mal leído aparece a los dos lados: antes del primer nombre y
  // después del último. Se saltean los pedazos cortos del principio y se corta
  // en el primero que aparezca DESPUÉS de un nombre de verdad. Cortar en el
  // primero a secas dejaba los nombres vacíos cuando el ruido venía adelante.
  let inicio = 0;
  while (inicio < partes.length && partes[inicio].length <= 2) inicio++;
  const resto = partes.slice(inicio);
  const corte = resto.findIndex((p) => p.length <= 2);
  return (corte >= 0 ? resto.slice(0, corte) : resto).join(" ").trim();
}

/**
 * Parte la línea de nombres del TD1 en apellido y nombres.
 *
 * El separador es DOBLE. Un apellido compuesto lleva un '<' simple entre sus
 * palabras, y los nombres también: en "GONZALEZ<RODRIGUEZ<<MARIANA<FL" el
 * apellido es "GONZALEZ RODRIGUEZ" y los nombres "MARIANA FL".
 *
 * Cortar en el primer '<' —que es lo que hacíamos— mandaba el segundo apellido
 * a los nombres: devolvía apellido "GONZALEZ" y nombres "RODRIGUEZ MARIANA".
 *
 * Cuando el OCR corrompe los rellenos no hay '<' que buscar, y ahí se recae en
 * el primer corte que haya: aproximado a propósito, porque el matching serio lo
 * hace `nameMatchesMrz` contra `nameLineRaw`.
 */
function partirNombres(l3Raw: string): { surname: string; givenNames: string } {
  return namesFrom(l3Raw);
}

/**
 * Cuántas veces la línea de nombres viola la FORMA que manda el TD1.
 *
 * La línea 3 es `APELLIDO<<NOMBRES` rellenada con '<' hasta 30. Eso quiere
 * decir que, sacado el relleno del final, adentro solo puede haber un '<<' —el
 * que separa apellido de nombres— y '<' sueltos entre palabras. Cualquier otra
 * corrida de '<' es ruido del OCR.
 *
 * No es una heurística ajustada a nuestras fotos: es el estándar. Sirve para
 * desempatar dos lecturas de la MISMA línea cuando los dígitos de control no
 * pueden hacerlo — y no pueden, porque la línea 3 del TD1 no tiene dígito
 * propio ni entra en el compuesto.
 *
 * Medido: "ROVIRA<<RODOLFOSMANUEL<<<<<<LK" da 4 y "ROVIRA<<RODOLFO<MANUEL" da 0.
 */
export function defectosDeLaLineaDeNombres(line3: string): number {
  const sinRelleno = line3.replace(/<+$/, "");
  let defectos = (sinRelleno.match(/[^A-Z<]/g) ?? []).length;
  let vistoSeparador = false;
  for (const corrida of sinRelleno.match(/<+/g) ?? []) {
    if (!vistoSeparador && corrida.length === 2) {
      vistoSeparador = true;
      continue;
    }
    defectos += corrida.length - 1;
  }
  return defectos;
}

export function extractMrz(rawOcrText: string): MrzTd1Data | null {
  // Camino feliz: un MRZ que ya vino limpio se parsea tal cual.
  const limpias = findMrzLines(rawOcrText);
  if (limpias) {
    const d = parseMrzTd1(limpias);
    if (d && isMrzLegible(d)) return d;
  }

  const lineas = rawOcrText
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, "").toUpperCase())
    .filter((l) => l.length >= 10);

  // Línea 2: la forma más reconocible del TD1, y la que trae la fecha.
  const RE_L2 = /(\d{6})(\d)([MFX])(\d{6})(\d)([A-Z]{3})/;
  let l2: RegExpMatchArray | null = null;
  let l2Idx = -1;
  for (let i = 0; i < lineas.length; i++) {
    const m = lineas[i].match(RE_L2);
    if (m) {
      l2 = m;
      l2Idx = i;
      break;
    }
  }

  // El PRIMER carácter de la línea 2 es la posición más frágil del MRZ: está
  // pegada al margen izquierdo, y en el modelo 2026 tiene al lado el número de
  // referencia dentro de un círculo. Medido sobre 2026-dorso.jpg: el "0" de
  // "0107998F" sale como "D" o se pierde entre la "C" del círculo, y quedan
  // seis dígitos donde el formato pide siete.
  //
  // No se adivina: se prueban los diez dígitos posibles y se acepta solo si UNO
  // cierra contra el verificador de la fecha de nacimiento. El verificador es lo
  // que convierte esto en una reconstrucción y no en una invención.
  if (!l2 || l2Idx < 0) {
    const RE_L2_CORTA = /(\d{5})(\d)([MFX])(\d{6})(\d)([A-Z]{3})/;
    for (let i = 0; i < lineas.length && !l2; i++) {
      const m = lineas[i].match(RE_L2_CORTA);
      if (!m) continue;
      const candidatos = "0123456789"
        .split("")
        .filter((d) => checkEquals(d + m[1], m[2]));
      if (candidatos.length !== 1) continue;
      const nacimiento = candidatos[0] + m[1];
      l2 = [m[0], nacimiento, m[2], m[3], m[4], m[5], m[6]] as unknown as RegExpMatchArray;
      l2Idx = i;
    }
  }
  if (!l2 || l2Idx < 0) return null;

  // Línea 1: ID + país + número + su verificador. El número del DNI es
  // numérico, así que el verificador es el dígito que queda suelto detrás.
  // El `[^0-9]{0,2}` entre el país y el número no es cosmético: según el
  // preproceso, el OCR mete una letra espuria ahí (se midió "IDARGT12345678").
  const RE_L1 = /ID([A-Z<]{3})[^0-9]{0,2}(\d{6,9})[^0-9]{0,4}(\d)/;

  // El `[^0-9]{0,2}` se come en silencio un dígito que el OCR leyó como letra, y
  // eso MUTILA el número. Medido sobre DNI-Argentina-2020-Reverso-01.jpg: el "8"
  // inicial de 81544670 sale como "B", el parser se lo traga y devuelve 1544670,
  // siete dígitos en vez de ocho. Con el número mal, nada del dorso sirve.
  //
  // Así que se prueba también la línea con las confusiones típicas del OCR
  // pasadas a dígito, y se elige por el verificador. Si ninguna cierra —pasa con
  // los especímenes publicados, que traen los verificadores inventados— se
  // prefiere la que NO perdió dígitos: es mejor mostrar el número entero y
  // decir que no verifica, que mostrar uno mutilado.
  const candidatosL1: RegExpMatchArray[] = [];
  for (let i = 0; i <= l2Idx; i++) {
    const linea = lineas[i];
    const variantes = [linea];
    // La reparación se aplica DESPUÉS del país, nunca antes: tocarlo convierte
    // "ARG" en "AR6" y la línea deja de reconocerse.
    const conPais = linea.match(/^(.*?ID[A-Z<]{3})(.*)$/);
    if (conPais) variantes.push(conPais[1] + digitosDeZonaNumerica(conPais[2]));
    for (const variante of variantes) {
      const m = variante.match(RE_L1);
      if (m) candidatosL1.push(m);
    }
  }
  if (candidatosL1.length === 0) return null;

  const cierra = (m: RegExpMatchArray) =>
    checkEquals((m[2] + "<<<<<<<<<").slice(0, 9), m[3]);
  const l1 =
    candidatosL1.find(cierra) ??
    candidatosL1.reduce((mejor, m) => (m[2].length > mejor[2].length ? m : mejor));

  // El compuesto vive después del país, al final de la línea 2. Si el OCR lo
  // perdió (lo leyó como letra) queda null: ausente no es lo mismo que mal.
  const colaL2 = lineas[l2Idx].slice(lineas[l2Idx].indexOf(l2[0]) + l2[0].length);
  const digitosCola = colaL2.replace(/[^0-9]/g, "");
  const compuesto = digitosCola.length > 0 ? digitosCola[digitosCola.length - 1] : null;

  // Línea de nombres: la de después de la 2 con letras suficientes.
  const l3Raw =
    lineas.slice(l2Idx + 1).find((l) => (l.match(/[A-Z]/g) ?? []).length >= 4) ?? "";

  const docNumber = l1[2];
  const docField = (docNumber + "<<<<<<<<<").slice(0, 9);
  const canon1 = ("ID" + l1[1] + docField + l1[3]).padEnd(30, "<").slice(0, 30);
  const canon2 = (
    l2[1] + l2[2] + l2[3] + l2[4] + l2[5] + l2[6] + "<".repeat(11) + (compuesto ?? "<")
  ).slice(0, 30);
  const canon3 = l3Raw.padEnd(30, "<").slice(0, 30);

  const d = parseMrzTd1([canon1, canon2, canon3]);
  if (!d) return null;

  const { surname, givenNames } = partirNombres(l3Raw);
  return {
    ...d,
    documentNumber: docNumber,
    surname,
    givenNames,
    nameLineRaw: l3Raw.replace(/[^A-Z]/g, ""),
    checks: { ...d.checks, composite: compuesto === null ? null : d.checks.composite },
  };
}
